import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  contractDetailQueryOptions,
  contractEventsQueryOptions,
  contractKeys,
  projectsFullListQueryOptions,
  workPackageListQueryOptions,
} from "@/lib/queries";
import { api, getToken } from "@/lib/api";
import { BillingTermsGrid } from "@/grids/BillingTermsGrid";
import { ContactsSection } from "@/components/ContactsSection";
import { useConfirmAction } from "@/components/ConfirmDialog";
import { EventTimeline } from "@/components/EventTimeline";
import { InlineEditField } from "@/components/InlineEditField";
import { SuggestionsAutocomplete } from "@/components/SuggestionsAutocomplete";
import { useToast } from "@/components/Toaster";
import { ProjectIdSelect, STANDARD_PROJECT_STATUSES } from "@/components/ProjectIdSelect";
import type { Contract } from "@/lib/types";

export const Route = createFileRoute("/_auth/contracts/$contractId")({
  component: ContractDetailPage,
});

function ContractDetailPage() {
  const { contractId } = Route.useParams();
  const navigate = useNavigate();
  const contract = useQuery(contractDetailQueryOptions(contractId));
  const events = useQuery(contractEventsQueryOptions(contractId));
  const sourceWorkspaces = useQuery({
    ...workPackageListQueryOptions({
      projectId: contract.data?.project_id ?? undefined,
      limit: 50,
    }),
    enabled: Boolean(contract.data?.project_id),
  });
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const deleteContract = useMutation({
    mutationFn: async () => {
      await api(`/api/contracts/${contractId}`, { method: "DELETE" });
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: contractKeys.detail(contractId) });
      qc.invalidateQueries({ queryKey: contractKeys.all });
      toast.success("Contract deleted");
      navigate({ to: "/contracts" });
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });

  const openSourceDocument = async (contractId: string) => {
    const token = getToken();
    const res = await fetch(`/api/contracts/${contractId}/document`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      toast.error("Could not load document");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const requestDelete = () => {
    if (!contract.data) return;
    const c = contract.data;
    const linkedTerms = c.billing_terms.filter(
      (t) => t.linked_plan_id || t.linked_subscription_id,
    );
    confirmAction({
      title: "Delete this contract?",
      message:
        `${c.contract_no}\n${c.title}\n\n` +
        `This will remove the contract, its ${c.billing_terms.length} billing terms, ` +
        `event log, and contact links.\n` +
        (linkedTerms.length > 0
          ? `\n⚠ ${linkedTerms.length} billing term(s) are linked to a Plan/Subscription ` +
            `the Agent created. Those Plans, Subscriptions, and any posted Journals/Invoices ` +
            `will REMAIN — only the contract row is deleted. Cancel/void those separately ` +
            `if you also want them gone.\n`
          : "") +
        `\nThe customer party (and other contracts/invoices it owns) will not be touched.\n` +
        `\nThis cannot be undone.`,
      confirmLabel: "Delete contract",
      kind: "danger",
      onConfirm: async () => {
        await deleteContract.mutateAsync();
      },
    });
  };

  const updateContract = useMutation({
    mutationFn: (patch: Partial<Contract>) =>
      api<Contract>(`/api/contracts/${contractId}`, {
        method: "PATCH",
        body: patch,
      }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: contractKeys.detail(contractId) });
      const prev = qc.getQueryData<Contract>(contractKeys.detail(contractId));
      if (prev) {
        qc.setQueryData<Contract>(contractKeys.detail(contractId), {
          ...prev,
          ...patch,
        });
      }
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(contractKeys.detail(contractId), ctx.prev);
      }
      toast.error(`Save failed: ${err.message}`);
    },
    onSuccess: (updated) => {
      qc.setQueryData(contractKeys.detail(contractId), updated);
      qc.invalidateQueries({ queryKey: contractKeys.events(contractId) });
      toast.success("Saved");
    },
  });

  if (contract.isLoading) {
    return <div className="p-8 text-sm text-slate-500 dark:text-slate-400">Loading contract…</div>;
  }
  if (contract.error) {
    return <div className="p-8 text-sm text-red-600">Error: {(contract.error as Error).message}</div>;
  }
  if (!contract.data) return null;

  const c = contract.data;
  const sourceWorkspace = sourceWorkspaces.data?.items.find(
    (item) => item.contract_id === c.id,
  );

  const statusColor =
    c.status === "active" ? "bg-green-100 text-green-800"
    : c.status === "draft" ? "bg-slate-100 text-slate-700"
    : c.status === "canceled" || c.status === "expired" ? "bg-red-100 text-red-700"
    : "bg-blue-100 text-blue-800";

  // Helper that returns an onSave fn for one field
  const saveField =
    <K extends keyof Contract>(field: K) =>
    async (newValue: string | number | null) => {
      await updateContract.mutateAsync({ [field]: newValue } as Partial<Contract>);
    };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-auto">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="text-xs text-slate-500 mb-1 dark:text-slate-400">
          <Link to="/contracts" className="hover:text-slate-700">Contracts</Link>
          <span className="mx-2">/</span>
          <span className="font-medium text-slate-700 dark:text-slate-300">{c.contract_no}</span>
        </div>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{c.title}</h1>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor}`}>
            {c.status}
          </span>
          <div className="flex-1" />
          {sourceWorkspace && (
            <Link
              to="/contract-work-packages/$packageId"
              params={{ packageId: sourceWorkspace.id }}
              className="inline-flex items-center gap-1.5 text-sm font-medium bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-md px-3 py-1.5 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800"
              title="Review the owner schedule of values, internal mappings, findings, and source evidence"
            >
              Review SOV &amp; evidence
            </Link>
          )}
          {c.source_document_url && (
            c.source_document_url.startsWith("local://") ? (
              <button
                type="button"
                onClick={() => openSourceDocument(c.id)}
                className="inline-flex items-center gap-1.5 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 dark:bg-gray-800 dark:text-slate-300"
                title="View source contract"
              >
                📄 View source contract
              </button>
            ) : (
              <a
                href={c.source_document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 dark:bg-gray-800 dark:text-slate-300"
                title={c.source_document_url}
              >
                📄 View source contract
              </a>
            )
          )}
          <button
            type="button"
            onClick={requestDelete}
            disabled={deleteContract.isPending}
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-md px-3 py-1.5 disabled:opacity-50"
            title="Delete contract (cascades billing terms, events, contact links)"
          >
            🗑 Delete
          </button>
        </div>
      </div>

      <div className="flex-1 px-6 py-4 space-y-6">
        {/* Metadata card — fields are editable inline */}
        <section className="bg-white rounded-lg border border-slate-200 p-4 dark:bg-gray-900 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 dark:text-slate-300">
            Contract Info
            <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
              click any field to edit
            </span>
          </h2>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <InlineEditField label="Contract No" value={c.contract_no} readOnly
              onSave={async () => {}} />
            <InlineEditField
              label="Signed By" value={c.signed_by_name}
              sub={c.signed_by_title}
              onSave={saveField("signed_by_name")}
            />
            <InlineEditField
              label="Signed By Title" value={c.signed_by_title}
              onSave={saveField("signed_by_title")}
            />
            <InlineEditField
              label="Signed Date" value={c.signed_date} type="date"
              onSave={saveField("signed_date")}
            />
            <InlineEditField
              label="Agreement Start" value={c.agreement_start_date} type="date"
              onSave={saveField("agreement_start_date")}
            />
            <InlineEditField
              label="Go-Live" value={c.go_live_date} type="date"
              onSave={saveField("go_live_date")}
            />
            <SuggestionsAutocomplete
              label="Governing Law"
              value={c.governing_law}
              fieldKey="contract.governing_law"
              onSave={(v) => saveField("governing_law")(v)}
            />
            <SuggestionsAutocomplete
              label="Payment Method"
              value={c.payment_method}
              fieldKey="contract.payment_method"
              onSave={(v) => saveField("payment_method")(v)}
            />
            <InlineEditField
              label="Cancellation Notice (days)"
              value={c.cancellation_notice_days} type="number"
              onSave={saveField("cancellation_notice_days")}
            />
            <InlineEditField
              label="Minimum Term (months)"
              value={c.minimum_term_months} type="number"
              onSave={saveField("minimum_term_months")}
            />
            <ProjectLinkField
              currentProjectId={c.project_id}
              onSave={(v) => saveField("project_id")(v)}
            />
          </dl>
          {c.agent_notes && (
            <details className="mt-4">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 dark:text-slate-400">
                Agent notes
              </summary>
              <pre className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 whitespace-pre-wrap dark:bg-gray-950 dark:border-gray-800">
                {c.agent_notes}
              </pre>
            </details>
          )}
        </section>

        {/* Contacts on this contract — extracted by the LLM at ingest */}
        <ContactsSection
          mode="links"
          links={c.contacts}
          targetKind="contract"
          targetId={c.id}
          partyId={c.customer_id ?? ""}
          title="Contract contacts"
          emptyMessage="No contacts on this contract yet. Click + Link contact to attach an existing contact, or add one from the customer page first."
        />

        {/* Billing terms grid — cells with ✎ are editable */}
        <section className="bg-white rounded-lg border border-slate-200 p-4 dark:bg-gray-900 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 dark:text-slate-300">
            Billing Terms
            <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
              {c.billing_terms.length} terms · ✎ columns are editable
            </span>
          </h2>
          <BillingTermsGrid contractId={c.id} terms={c.billing_terms} />
          <p className="text-xs text-slate-500 mt-3 dark:text-slate-400">
            Double-click an ✎ cell to edit. Enter saves, Esc cancels.
            Changes persist immediately and append a row to the Fact Log below.
          </p>
        </section>

        {/* Event timeline */}
        <section className="bg-white rounded-lg border border-slate-200 p-4 dark:bg-gray-900 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 dark:text-slate-300">
            Fact Log
            <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
              {events.data?.length ?? 0} events · the AI substrate
            </span>
          </h2>
          {events.isLoading && <div className="text-sm text-slate-500 dark:text-slate-400">Loading events…</div>}
          {events.data && <EventTimeline events={events.data} />}
        </section>
      </div>
    </div>
  );
}


/**
 * Inline project picker for the contract's `project_id` FK.
 *
 * - "(none)" + Edit → opens a small select with all projects in the
 *   current entity, plus a "(none — master agreement)" option that
 *   clears the link.
 * - When set, shows the project code+name as a direct link to the project.
 */
function ProjectLinkField({
  currentProjectId,
  onSave,
}: {
  currentProjectId: string | null;
  onSave: (newId: string | null) => Promise<void> | void;
}) {
  const projsQuery = useQuery(projectsFullListQueryOptions({ limit: 500 }));
  const projects = projsQuery.data?.items ?? [];
  const current = projects.find((p) => p.id === currentProjectId);
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(currentProjectId ?? "");

  if (!editing) {
    return (
      <div>
        <dt className="text-xs uppercase tracking-wide text-slate-500 mb-1 dark:text-slate-400">
          Linked project
        </dt>
        <dd>
          {current ? (
            <Link
              to="/projects/$projectId"
              params={{ projectId: current.id }}
              className="text-sm text-blue-700 hover:underline"
              title={current.name}
            >
              <span className="font-mono text-xs">{current.code}</span>{" "}
              {current.name}
            </Link>
          ) : (
            <span className="text-sm text-slate-400 italic dark:text-slate-500">(none — master / unattached)</span>
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="ml-2 text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400"
          >
            edit
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 mb-1 dark:text-slate-400">
        Linked project
      </dt>
      <dd className="flex items-center gap-1">
        <div className="min-w-[320px]">
        <ProjectIdSelect
          size="small"
          value={pick}
          onChange={setPick}
          statusFilter={STANDARD_PROJECT_STATUSES}
          label="Linked project"
          placeholder="None — master / unattached…"
        />
        </div>
        <button
          type="button"
          onClick={async () => {
            await onSave(pick || null);
            setEditing(false);
          }}
          className="text-xs text-blue-700 hover:underline"
        >
          save
        </button>
        <button
          type="button"
          onClick={() => {
            setPick(currentProjectId ?? "");
            setEditing(false);
          }}
          className="text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400"
        >
          cancel
        </button>
      </dd>
    </div>
  );
}
